import type { AdvisorDraft, AdvisorDraftItem, AdvisorProduct } from './types';

// Generate items carry no price, map point or duration: the planner looks
// their products up (`products.list` `ids`) and writes those in.

/** Products looked up per request (the portal lookup's cap). */
export const DETAILS_LOOKUP_LIMIT = 30;

/**
 * The item still needs its catalogue details: flagged by Generate, or a
 * Generate item saved before the flag (no currency and no map point).
 */
export const needsDetails = (item: AdvisorDraftItem): boolean =>
  item.detailsPending === true ||
  (item.detailsPending === undefined &&
    item.origin === 'suggested' &&
    item.currency == null &&
    !item.location);

/**
 * A base hotel saved before XT-827 carries no historical average (not even
 * `null`), so the estimate cannot use it: look its product up once.
 */
export const needsAverageLookup = (hotel: AdvisorDraftItem): boolean =>
  hotel.avgRoomNight === undefined && hotel.detailsPending !== false;

/** Product ids of rail items still needing details, in rail order. */
export const pendingDetailIds = (draft: AdvisorDraft): string[] => {
  const ids = new Set<string>();
  for (const leg of draft.legs) {
    if (
      leg.baseHotel &&
      (needsDetails(leg.baseHotel) || needsAverageLookup(leg.baseHotel))
    ) {
      ids.add(leg.baseHotel.productId);
    }
    for (const day of leg.days) {
      for (const item of day.items) {
        if (needsDetails(item)) ids.add(item.productId);
      }
    }
  }
  return [...ids];
};

/** The item with the catalogue's price, unit, map point and duration. */
export const withProductDetails = (
  item: AdvisorDraftItem,
  product: AdvisorProduct | undefined
): AdvisorDraftItem =>
  product
    ? {
        ...item,
        clientPrice: product.clientPrice,
        childPrice: product.childPrice ?? null,
        currency: product.currency,
        thumbnail: product.thumbnail ?? item.thumbnail,
        ...(product.location ? { location: product.location } : {}),
        ...(product.pricingType ? { pricingType: product.pricingType } : {}),
        ...(product.durationMinutes
          ? { durationMinutes: product.durationMinutes }
          : {}),
        // `null` records "looked up, no history", so it is not asked again.
        avgRoomNight: product.avgRoomNight ?? null,
        detailsPending: false,
      }
    : item;

/**
 * Writes a lookup's rows into the items it was asked for. An asked-for
 * product the lookup did not return (hidden, deleted) is marked looked up
 * too, so it is not asked for again. Nothing to change returns the draft.
 */
export const fillItemDetails = (
  draft: AdvisorDraft,
  requested: readonly string[],
  products: readonly AdvisorProduct[]
): AdvisorDraft => {
  const asked = new Set(requested);
  const byId = new Map(products.map((product) => [product.id, product]));
  let changed = false;
  const fill = (
    item: AdvisorDraftItem,
    needs: (item: AdvisorDraftItem) => boolean = needsDetails
  ): AdvisorDraftItem => {
    if (!needs(item) || !asked.has(item.productId)) return item;
    changed = true;
    const product = byId.get(item.productId);
    return product
      ? withProductDetails(item, product)
      : { ...item, detailsPending: false, avgRoomNight: null };
  };
  const baseHotelNeeds = (item: AdvisorDraftItem) =>
    needsDetails(item) || needsAverageLookup(item);
  const legs = draft.legs.map((leg) => ({
    ...leg,
    ...(leg.baseHotel
      ? { baseHotel: fill(leg.baseHotel, baseHotelNeeds) }
      : {}),
    days: leg.days.map((day) => ({
      ...day,
      items: day.items.map((item) => fill(item)),
    })),
  }));
  return changed ? { ...draft, legs } : draft;
};
